import * as React from 'react';
import { parse } from 'query-string';
import { Component } from 'react';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { AwardCard } from 'Src/components/AwardCard/AwardCard';
import { ParentFilterBar } from 'Src/components/ParentFilterBar/ParentFilterBar';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  getSearchFilter
} from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, CARD_LIMIT } from 'Src/consts/table';
import { getStudentAwardsCurrentOverview, AwardItem } from 'Src/helpers/service/student/awards';
import { StudentPageHeader } from '../components/StudentPageHeader';
import '../components/StudentHub.scss';
import '../../ParentView/components/ParentLists.scss';
import './Awards.scss';

const DEFAULT_SEARCH = {
  order: 'awardedAt:DESC'
};

const FILTER_FIELDS_PANEL: ColumnDefinition[] = [
  { text: 'Award type', field: 'awardType.name', type: FILTER_TYPE.TEXT, accessor: [''], isSort: false },
  { text: 'Awarded at', field: 'awardedAt', type: FILTER_TYPE.DATE_INTERVAL, accessor: [''], isSort: false }
];

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  items: AwardItem[];
  total: number;
  points: number;
  academicYear: string;
  currentPage: number;
  filters: any;
  sortFieldName: string;
  sortDirection: string;
  isFiltersOpen: boolean;
  isLoading: boolean;
  basePath: string;
}

export class StudentAwardsCurrent extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      total: 0,
      points: 0,
      academicYear: '',
      currentPage: FIRST_PAGE,
      filters: {},
      sortFieldName: '',
      sortDirection: '',
      isFiltersOpen: false,
      isLoading: false,
      basePath: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true }, this.setItems);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true }, this.setItems);
    }
  }

  setItems = () => {
    const { history, user } = this.props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortFieldName = '';
    if (typeof DEFAULT_SEARCH.order === 'string') {
      [sortFieldName, sortDirection] = DEFAULT_SEARCH.order.split(':');
    }

    const filters = getFilters2(FILTER_FIELDS_PANEL, search);
    const where = { ...filters };

    const order = `${sortFieldName} ${sortDirection}`;
    const serverQueryFilter = getServerQueryFilter2(page, where, order, CARD_LIMIT);

    return getStudentAwardsCurrentOverview(user, serverQueryFilter).then(overview => {
      this.setState({
        items: overview.items,
        total: overview.total,
        points: overview.points,
        academicYear: overview.academicYear,
        currentPage: page,
        filters,
        sortDirection,
        sortFieldName,
        basePath: history.location.pathname,
        isLoading: false
      });
    });
  };

  setCurrentPageParams = (currentPage: number): void => {
    const { filters } = this.state;
    const parts: string[] = [];
    if (currentPage !== 1) parts.push(`page=${currentPage}`);
    if (isFilterExist2(filters)) parts.push(getSearchFilter(filters));
    this.props.history.push({ pathname: this.state.basePath, search: parts.join('&') });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event?.target?.value ?? '';
    const { filters } = this.state;
    const current = filters[filterField];
    let next = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          next = { ...next, [filterField]: { ...current, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          next = { ...next, [filterField]: { ...current, to: filterValue } };
          break;
      }
    } else {
      next = { ...next, [filterField]: filterValue };
    }

    this.setState({ filters: next });
  };

  onApplyFilterClick = (): void => {
    const { filters } = this.state;
    const parts: string[] = [];
    if (isFilterExist2(filters)) parts.push(getSearchFilter(filters));
    this.props.history.push({ pathname: this.state.basePath, search: parts.join('&') });
  };

  onClearFilterClick = (): void => {
    this.props.history.push({ pathname: this.state.basePath, search: '' });
  };

  render() {
    const { isLoading, items, total, points, academicYear, currentPage, filters, isFiltersOpen } = this.state;

    if (isLoading) return <Loader />;

    const from = total === 0 ? 0 : (currentPage - 1) * CARD_LIMIT + 1;
    const to = Math.min(currentPage * CARD_LIMIT, total);
    const isFiltered = isFilterExist2(filters);

    return (
      <main className="parentPage parentListPage studentHubPage awardsPage">
        <StudentPageHeader
          title="Awards & House Points"
          subtitle="Your recognition and points from the current academic year."
          chips={[
            { value: total, label: total === 1 ? 'award' : 'awards', tone: 'blue' },
            { value: points, label: 'house points', tone: 'green' },
            { label: academicYear || 'Current academic year', tone: 'slate' }
          ]}
        />

        <div className="parentListControls">
          <button
            type="button"
            className={isFiltersOpen ? 'parentFilterButton is-active' : 'parentFilterButton'}
            onClick={() => this.setState(prev => ({ isFiltersOpen: !prev.isFiltersOpen }))}
          >
            <span className="parentFilterButton__icon" aria-hidden="true" /> Filters
          </button>

          <Pagination2
            modern
            currentPage={currentPage}
            total={total}
            limit={CARD_LIMIT}
            setCurrentPageParams={this.setCurrentPageParams}
          />
        </div>

        {isFiltersOpen && (
          <div className="awardsFilterPanel">
            <ParentFilterBar
              filters={filters}
              onTableFilterChange={this.onTableFilterChange}
              onApply={this.onApplyFilterClick}
              onClear={this.onClearFilterClick}
              onCancel={() => this.setState({ isFiltersOpen: false })}
              labels={{ text: 'Award type', date: 'Awarded date' }}
              fieldKeys={{ textKey: 'awardType.name', dateKey: 'awardedAt' }}
            />
          </div>
        )}

        <section className="awardsListCard">
          <div className="awardsToolbar">
            <div>
              <h2>
                Awards <span>{total}</span>
              </h2>
              <p>
                {isFiltered
                  ? 'Filtered current academic year awards.'
                  : 'Current academic year awards sorted by most recent.'}
              </p>
            </div>
            <div className="awardsToolbar__meta">
              {from}-{to} of {total}
            </div>
          </div>

          {items.length > 0 ? (
            <div className="awardsGrid">
              {items.map(item => (
                <AwardCard item={item} hideStudent user={this.props.user} key={item.id} />
              ))}
            </div>
          ) : (
            <div className="awardsEmpty">
              <div>★</div>
              <h3>No awards found</h3>
              <p>No awards match the current academic year and filters.</p>
            </div>
          )}
        </section>
      </main>
    );
  }
}
